import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { AsnStatistic } from '../../types/api';

interface AsnChartProps {
  data: AsnStatistic[];
  loading?: boolean;
}

export const AsnChart: React.FC<AsnChartProps> = ({ data, loading = false }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
        <Typography variant="body2" color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  const chartData = data.map((asn) => {
    let label = `AS${asn.asn}`;
    if (asn.organization) {
      const orgName = asn.organization.length > 25
        ? asn.organization.substring(0, 22) + '...'
        : asn.organization;
      label = `${label}: ${orgName}`;
    }
    return { asn: label, events: asn.count };
  });

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} horizontal={false} />
        <XAxis
          type="number"
          stroke={theme.palette.text.secondary}
          style={{ fontSize: '0.75rem' }}
          tickLine={false}
        />
        <YAxis
          dataKey="asn"
          type="category"
          stroke={theme.palette.text.secondary}
          style={{ fontSize: '0.7rem' }}
          tickLine={false}
          width={180}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderRadius: 8,
            fontSize: '0.8125rem',
          }}
        />
        <Bar
          dataKey="events"
          name="Events"
          fill={theme.palette.info.main}
          radius={[0, 4, 4, 0]}
          maxBarSize={24}
          fillOpacity={0.85}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};
